import {
  Icon,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { UserContext, LanguageContext } from "App";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useLocation, useNavigate } from "react-router-dom";
import { useContext, useCallback } from "react";
import AddFacilityModal from "./AddFacilityModal";
import AppHourCell2 from "./AppHourCell2";
import CreateAppointmentPopUp from "./CreateAppointmentPopUp";
import Loader from "layouts/loader/loader";
import AddTherapistToDay from "../../AppointmentDays/AppointmentDayDetails/AddTherapistToDay";
import CopyDayCard from "./CopyDayCard";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import TherapistDetailsPopUp from "./TherapistDetailsPopUp";
import EvalPatientsModal from "./EvalPatientsModal";
import DummySessionsModal from "./DummySessionsModal";
import { SYSTEM_COLORS } from "../../../../utils/themeConstants";
import { useAppDayLogic } from "./useAppDayLogic";
import TherapistReorderModal from "./TherapistReorderModal";
import "./AppDay.scss";
import { commonFlexStyles } from "../../../../utils/themeConstants";

const AppDayTableSimple = (props) => {
  const {
    AppDay,
    setAppDay,
    setIsLoaded,
    GetAppDayById,
    DayPartitionNumber,
    ShowCopyDay,
    setShowCopyDay,
    ShowAddTherapist,
    setShowAddTherapist,
  } = props;
  const { state, search } = useLocation();
  const navigate = useNavigate();
  const CurrentUser = useContext(UserContext);
  const { translations } = useContext(LanguageContext);

  const logic = useAppDayLogic({
    ...props,
    state,
    search,
    navigate,
    translations,
  });

  const tableContainerRef = useCallback(
    (node) => {
      if (node !== null) {
        const handleScroll = (e) => {
          if (e.target.scrollTop > 50 && !logic.isCollapsed) {
            logic.setIsCollapsed(true);
          }
        };
        node.addEventListener("scroll", handleScroll);
        return () => node.removeEventListener("scroll", handleScroll);
      }
    },
    [logic.isCollapsed, logic.setIsCollapsed],
  );
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      logic.onSearch();
    }
  };

  const MakeHeaderRow = () => {
    const headerArr = [];

    const hourStats = {};
    AppDay?.getTherapists?.forEach((therapist) => {
      therapist.getAppointmentHours?.forEach((hourData) => {
        const hr = hourData.hour;
        if (!hourStats[hr]) hourStats[hr] = {};

        const appointments = hourData.getListedAccountingSessions;

        appointments.forEach((app) => {
          if (app) {
            const type = app.getSession.category || "Session Name";
            hourStats[hr][type] = (hourStats[hr][type] || 0) + 1;
          }
        });
      });
    });

    for (let i = 0; i < DayPartitionNumber * 8; i++) {
      if (i >= (DayPartitionNumber - 1) * 8) {
        const hourString = i < 10 ? `0${i}:00` : `${i}:00`;

        const sessions = hourStats[i];
        let tooltipValue = "";

        if (sessions && Object.keys(sessions).length > 0) {
          tooltipValue = Object.entries(sessions)
            .map(([name, count]) => `${name}: ${count}`)
            .join("\n");
        } else {
          tooltipValue = "No Sessions Scheduled";
        }

        headerArr.push(
          <th
            key={i}
            className="dashed-hour-header"
            data-tooltip={tooltipValue}
          >
            {hourString}
          </th>,
        );
      }
    }
    return headerArr;
  };

  const MakeTableRow = (tDay) => {
    if (
      CurrentUser.userType === "Therapist" &&
      tDay.therapistId !== CurrentUser.getTherapist?.id
    )
      return null;
    const appointmentHours = tDay.getAppointmentHours ?? [];
    const hasAppointments = appointmentHours.some(
      (ah) => ah.getAppointments?.length > 0,
    );
    const isRemoving = logic.removingTherapist === tDay.therapistId;

    return (
      <tr key={tDay.therapistId}>
        <td
          className="sticky-col sticky-col-1"
          style={{
            backgroundColor: SYSTEM_COLORS.tableStickyBg,
            color: SYSTEM_COLORS.primaryDark,
            padding: "0",
            borderRight: `2px solid ${SYSTEM_COLORS.tableStickyBorder}`,
            borderBottom: `1px solid ${SYSTEM_COLORS.tableStickyBorder}`,
            width: "220px",
            zIndex: 10,
            position: "sticky",
            left: 0,
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={3}
            sx={{ height: "100%" }}
          >
            <MDBox display="flex" flexDirection="column" sx={{ minWidth: 0 }}>
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{
                  color: SYSTEM_COLORS.dark,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {tDay.therapistName}
              </MDTypography>
            </MDBox>

            {["Admin", "Owner", "HeadAccounting", "AppointmentEmployee", "CallCenterEmployee"].includes(
              CurrentUser.userType,
            ) && (
              <MDBox display="flex" alignItems="center" gap={0.5}>
                {hasAppointments && (
                  <Icon
                    sx={{
                      color: SYSTEM_COLORS.warning,
                      fontSize: "1.1rem !important",
                    }}
                  >
                    warning
                  </Icon>
                )}
                <button
                  onClick={() => logic.removeTherapistFromDay(tDay)}
                  disabled={hasAppointments || isRemoving}
                  style={{
                    background: "rgba(255, 255, 255, 0.6)",
                    border: `1px solid ${SYSTEM_COLORS.danger}33`,
                    borderRadius: "8px",
                    width: "30px",
                    height: "30px",
                    ...commonFlexStyles,
                    transition: "all 0.2s",
                    color: hasAppointments
                      ? SYSTEM_COLORS.muted
                      : SYSTEM_COLORS.danger,
                  }}
                >
                  {isRemoving ? (
                    <CircularProgress
                      size={14}
                      thickness={6}
                      sx={{ color: SYSTEM_COLORS.danger }}
                    />
                  ) : (
                    <Icon fontSize="small">delete</Icon>
                  )}
                </button>
              </MDBox>
            )}
          </MDBox>
        </td>
        {appointmentHours
          .sort((a, b) => a.hourIndex - b.hourIndex)
          .map((appHour) => {
            if (
              appHour.hourIndex < DayPartitionNumber * 8 &&
              appHour.hourIndex >= (DayPartitionNumber - 1) * 8
            ) {
              return (
                <td key={appHour.id}>
                  <AppHourCell2
                    AppDay={AppDay}
                    AppHour={appHour}
                    GetAppDayById={GetAppDayById}
                    setSelectedAppHour={logic.setSelectedAppHour}
                    EnableFacilityModal={(en, sess) => {
                      logic.setFacilityAccSession(sess);
                      logic.setShowFacilityModal(en);
                    }}
                    EnableAppointmentModal={(en, hr) => {
                      logic.setSelectedAppHour(hr);
                      logic.setShowCreateAppointment(en);
                    }}
                    PasteSession={logic.PasteSession}
                    setMovingSession={logic._setMovingSession}
                    setIsLoaded={setIsLoaded}
                    Reminders={logic.Reminders}
                    LoadReminders={logic.GetDayReminders}
                    isOperationsEnabled={logic.isOperationsEnabled}
                  />
                </td>
              );
            }
            return null;
          })}
      </tr>
    );
  };

  const sidebarMargin = logic.ShowSideBar ? "250px" : "0px";

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#f0f2f5",
      }}
    >
      <MDBox
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        sx={{
          marginLeft: sidebarMargin,
          backgroundColor: SYSTEM_COLORS.white,
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          zIndex: 100,
          flexShrink: 0,
          transition: "margin-left 0.3s ease",
        }}
      >
        <div
          style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px 30px" }}
        >
          <MDBox
            className={`header-content ${logic.isCollapsed ? "collapsed" : ""}`}
            sx={{
              position: "relative",
              backgroundColor: SYSTEM_COLORS.white,
              transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              sx={{ p: "8px 20px" }}
            >
              <MDBox display="flex" alignItems="center" gap={2}>
                <MDButton
                  variant="gradient"
                  color="dark"
                  iconOnly
                  circular
                  size="small"
                  onClick={() => logic.setShowSideBar(!logic.ShowSideBar)}
                >
                  <Icon>{logic.ShowSideBar ? "close" : "menu"}</Icon>
                </MDButton>
                <MDBox>
                  <MDTypography
                    variant="button"
                    fontWeight="regular"
                    color="text"
                    display="block"
                    sx={{ lineHeight: 1 }}
                  >
                    Appointment Schedule
                  </MDTypography>
                  <MDTypography variant="h6" fontWeight="bold" sx={{ mt: 0.5 }}>
                    {new Date(AppDay?.dateDay ?? "").toLocaleDateString(
                      "en-GB",
                      {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      },
                    )}
                  </MDTypography>
                </MDBox>
              </MDBox>
              <MDBox
                display="flex"
                alignItems="center"
                sx={{
                  background: SYSTEM_COLORS.light,
                  padding: "4px 12px",
                  borderRadius: "50px",
                  border: `1px solid ${SYSTEM_COLORS.tableBorder}`,
                }}
              >
                <Icon
                  sx={{
                    cursor: "pointer",
                    fontSize: "1.2rem !important",
                    color: SYSTEM_COLORS.muted,
                  }}
                  onClick={() =>
                    logic.setZoomLevel((p) => Math.max(p - 0.05, 0.2))
                  }
                >
                  remove_circle_outline
                </Icon>
                <MDTypography
                  variant="button"
                  fontWeight="bold"
                  sx={{ mx: 1.5, minWidth: "40px", textAlign: "center" }}
                >
                  {Math.round(logic.zoomLevel * 100)}%
                </MDTypography>
                <Icon
                  sx={{
                    cursor: "pointer",
                    fontSize: "1.2rem !important",
                    color: SYSTEM_COLORS.primary,
                  }}
                  onClick={() =>
                    logic.setZoomLevel((p) => Math.min(p + 0.05, 1.0))
                  }
                >
                  add_circle_outline
                </Icon>
              </MDBox>
            </MDBox>

            <MDBox
              sx={{
                overflow: "hidden",
                transition: "all 0.6s",
                maxHeight: logic.isCollapsed ? "0px" : "800px",
                opacity: logic.isCollapsed ? 0 : 1,
              }}
            >
              <MDBox px="30px" pb="30px">
                {[
                  "Admin", "Owner", "HeadAccounting",
                  "AppointmentEmployee",
                  "CallCenterEmployee",
                  "SubAdmin",
                ].includes(CurrentUser.userType) && (
                  <MDBox display="flex" flexWrap="wrap" gap={1.5} mb={1}>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={() => setShowAddTherapist(!ShowAddTherapist)}
                    >
                      <Icon sx={{ mr: 1 }}>person_add</Icon> Therapist
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={() => logic.setShowTransferModal(true)}
                    >
                      <Icon sx={{ mr: 1 }}>compare_arrows</Icon> Move/Switch
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={() => setShowCopyDay(!ShowCopyDay)}
                    >
                      <Icon sx={{ mr: 1 }}>content_copy</Icon> Smart Copy
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={() => logic.setIsOrderModalOpen(true)}
                    >
                      <Icon sx={{ mr: 1 }}>person_add</Icon> Reorder Therapists
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      onClick={() => logic.setShowEvalModal(true)}
                    >
                      <Icon sx={{ mr: 1 }}>assignment_ind</Icon> Show Evals
                    </MDButton>
                    <MDButton
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={() => logic.setShowDummyModal(true)}
                      disabled={logic.dummySessionsList.length === 0}
                    >
                      <Icon sx={{ mr: 1 }}>visibility</Icon> Dummy (
                      {logic.dummySessionsList.length})
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={logic.ClearAppointmentDay}
                      sx={{ ml: { md: "auto" } }}
                    >
                      <Icon sx={{ mr: 1 }}>delete_sweep</Icon> Clear Sessions
                    </MDButton>
                  </MDBox>
                )}
                <MDBox display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={logic.isOperationsEnabled}
                        onChange={(e) =>
                          logic.setIsOperationsEnabled(e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <MDTypography variant="caption" fontWeight="bold">
                        {logic.isOperationsEnabled
                          ? "Operations On"
                          : "Operations Off"}
                      </MDTypography>
                    }
                  />
                </MDBox>
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  gap={3}
                  p={2}
                  sx={{
                    borderRadius: "16px",
                    border: `1px solid ${SYSTEM_COLORS.tableBorder}`,
                  }}
                >
                  <MDBox
                    display="flex"
                    sx={{
                      background: "rgba(0, 0, 0, 0.05)",
                      p: 0.5,
                      borderRadius: "12px",
                      width: "100%",
                      maxWidth: { md: "max-content" },
                      flexDirection: "row",
                      overflowX: "auto",
                      gap: 0.5,
                      "&::-webkit-scrollbar": { display: "none" },
                      msOverflowStyle: "none",
                      scrollbarWidth: "none",
                    }}
                  >
                    {[
                      { id: 1, label: "Night", range: "00-07" },
                      { id: 2, label: "Morning", range: "08-15" },
                      { id: 3, label: "Evening", range: "16-23" },
                    ].map((p) => {
                      const isActive = DayPartitionNumber === p.id;
                      return (
                        <MDButton
                          key={p.id}
                          size="small"
                          onClick={() => logic.handlePartitionChange(p.id)}
                          sx={{
                            flex: 1,
                            minWidth: { xs: "80px", sm: "120px", md: "160px" },
                            borderRadius: "10px",
                            textTransform: "none",
                            py: { xs: 0.5, md: 1 },
                            px: { xs: 1, md: 2 },
                            backgroundColor: isActive
                              ? `${SYSTEM_COLORS.white} !important`
                              : "transparent !important",
                            color: isActive
                              ? `${SYSTEM_COLORS.primaryDark} !important`
                              : SYSTEM_COLORS.muted,
                            boxShadow: isActive
                              ? "0 4px 6px -1px rgba(0,0,0,0.1)"
                              : "none",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <MDBox
                            flexDirection={{ xs: "column", md: "row" }}
                            width="100%"
                            sx={{ ...commonFlexStyles }}
                          >
                            <MDTypography
                              variant="button"
                              fontWeight={isActive ? "700" : "500"}
                              sx={{
                                fontSize: { xs: "0.7rem", md: "0.85rem" },
                                color: "inherit",
                                lineHeight: 1.2,
                              }}
                            >
                              {p.label}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{
                                fontSize: "0.6rem",
                                ml: { md: 1 },
                                mt: { xs: 0.2, md: 0 },
                                opacity: 0.6,
                                color: "inherit",
                                display: { xs: "none", sm: "block" },
                              }}
                            >
                              {p.range}
                            </MDTypography>
                          </MDBox>
                        </MDButton>
                      );
                    })}
                  </MDBox>
                  {[
                    "Admin", "Owner", "HeadAccounting",
                    "AppointmentEmployee",
                    "CallCenterEmployee",
                    "SubAdmin",
                  ].includes(CurrentUser.userType) && (
                    <MDBox
                      display="flex"
                      alignItems="center"
                      sx={{ width: { xs: "100%", md: "320px" } }}
                    >
                      <MDInput
                        fullWidth
                        size="small"
                        label="Search appointments..."
                        value={logic.SearchString}
                        onChange={(e) => logic.setSearchString(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px 0 0 10px",
                            backgroundColor: SYSTEM_COLORS.white,
                          },
                        }}
                      />
                      <MDButton
                        variant="gradient"
                        color="info"
                        onClick={logic.onSearch}
                        sx={{
                          borderRadius: "0 10px 10px 0",
                          minHeight: "37px",
                          boxShadow: "none",
                        }}
                      >
                        <Icon>search</Icon>
                      </MDButton>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>

            <MDBox
              onClick={() => logic.setIsCollapsed(!logic.isCollapsed)}
              sx={{
                position: "absolute",
                bottom: "-15px",
                left: "50%",
                transform: "translateX(-50%)",
                cursor: "pointer",
                background: SYSTEM_COLORS.primaryDark,
                color: SYSTEM_COLORS.white,
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                ...commonFlexStyles,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                zIndex: 10,
              }}
            >
              <Icon>{logic.isCollapsed ? "expand_more" : "expand_less"}</Icon>
            </MDBox>
          </MDBox>
        </div>
        {ShowCopyDay && (
          <MDBox mt={3}>
            <CopyDayCard
              CurrentDay={AppDay}
              setAppDay={setAppDay}
              GetAppDayById={GetAppDayById}
            />
          </MDBox>
        )}
        {ShowAddTherapist && (
          <AddTherapistToDay
            AppDay={AppDay}
            setIsLoaded={setIsLoaded}
            GetAppDayById={GetAppDayById}
          />
        )}
      </MDBox>

      <MDBox
        sx={{
          flexGrow: 1,
          marginLeft: sidebarMargin,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          transition: "margin-left 0.3s ease",
        }}
      >
        {logic.isInnerLoaded ? (
          <div
            ref={tableContainerRef}
            className="table-container"
            style={{
              flexGrow: 1,
              overflow: "auto",
              paddingBottom: "50px",
            }}
          >
            <table
              id="AppointmentMainTable"
              className="table table-bordere fixed-table"
              style={{
                zoom: `${logic.zoomLevel}`,
                transformOrigin: "top left",
                width: "100%",
              }}
            >
              <thead>
                <tr className="small-width text-center">
                  <th style={{ backgroundColor: SYSTEM_COLORS.tableHeaderBg }}>
                    Name / Time
                  </th>
                  {MakeHeaderRow()}
                </tr>
              </thead>
              <tbody className="small-width text-center font-weight-bold">
                {(AppDay?.getTherapists ?? []).map((tDay) =>
                  MakeTableRow(tDay),
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <MDBox sx={{ ...commonFlexStyles }} height="100%">
            <Loader />
          </MDBox>
        )}
      </MDBox>

      <AddFacilityModal
        setIsLoaded={setIsLoaded}
        ShowModal={logic.ShowFacilityModal}
        AccSession={logic.FacilityAccSession}
        EnableModal={logic.setShowFacilityModal}
      />
      <CreateAppointmentPopUp
        AppDay={AppDay}
        setAppDay={setAppDay}
        GetAppDayById={GetAppDayById}
        AppHour={logic.SelectedAppHour}
        ShowCreateAppointment={logic.ShowCreateAppointment}
        EnableModal={logic.setShowCreateAppointment}
        DayPartitionNumber={DayPartitionNumber}
        AddSession={logic.AddSessionToAppointmentHour}
      />
      <TherapistDetailsPopUp
        show={logic.showTransferModal}
        onClose={() => logic.setShowTransferModal(false)}
        therapists={AppDay?.getTherapists}
        appDayId={AppDay?.id}
        setIsLoaded={setIsLoaded}
      />
      <EvalPatientsModal
        open={logic.showEvalModal}
        onClose={() => logic.setShowEvalModal(false)}
        AppDay={AppDay}
      />
      <DummySessionsModal
        open={logic.showDummyModal}
        onClose={() => logic.setShowDummyModal(false)}
        dummySessions={logic.dummySessionsList}
      />
      <TherapistReorderModal
        open={logic.isOrderModalOpen}
        handleClose={() => logic.setIsOrderModalOpen(false)}
        therapists={AppDay.getTherapists}
        dayId={AppDay.id}
        refreshData={GetAppDayById}
      />
    </div>
  );
};

export default AppDayTableSimple;
